import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { BRAND } from '@/config/brand';

/**
 * Sprint 2 D5 — Klick-Tipp Opt-in Component.
 *
 * Embed-Stellen:
 *  - Quick-Check-Ergebnisseite  (quelle="quick-check-ergebnis")
 *  - Landing-Page-Footer        (quelle="landing-page-footer")
 *  - Anliegen-Co-Creator-Output (quelle="anliegen-co-creator-output")
 *
 * Klick-Tipp-Submit erfolgt direkt im Browser an
 *   https://app.klicktipp.com/api/subscriber/signin.html
 * 6 Custom-Fields werden als Hidden-Inputs mitgegeben. Parallel wird der
 * Lead an POST /api/lead-capture geschickt (non-blocking; Fehler darf den
 * KT-Submit nicht stoppen).
 */

const FOKUSTYP_TO_ZIELSEITE = {
  katze: '1',
  eule: '2',
  biene: '3',
  hund: '4',
};

const HEADLINES = {
  'quick-check-ergebnis': 'Ergebnisse + Mini-Perspektiven per Mail',
  'landing-page-footer': 'Bleib in Kontakt — 1 Mini-Impuls pro Woche',
  'anliegen-co-creator-output': 'Deine Formel als Erinnerung per Mail?',
};

const SUBTEXTS = {
  'quick-check-ergebnis':
    'Du bekommst dein Quick-Check-Ergebnis + 1 Mini-Impuls pro Woche. Jederzeit kündbar.',
  'landing-page-footer':
    'Wöchentliche Mini-Impulse zu den 4 Fokustypen. Jederzeit kündbar.',
  'anliegen-co-creator-output':
    'Du bekommst deine 4 Situations-Sätze + 1 Mini-Impuls pro Woche. Jederzeit kündbar.',
};

const KT_API_KEY = '88cxzlh9z8z2bad';
const KT_ACTION_URL = 'https://app.klicktipp.com/api/subscriber/signin.html';
const DATENSCHUTZ_URL = 'https://effektiv-kommunizieren.de/datenschutz';

export function KlickTippOptin({ quelle }) {
  const [fokustyp, setFokustyp] = useState('');
  const [fokustypScore, setFokustypScore] = useState('');
  const [quizDatum, setQuizDatum] = useState('');
  const [gdprTimestamp, setGdprTimestamp] = useState('');

  useEffect(() => {
    try {
      const ergebnis = sessionStorage.getItem('fokustyp_ergebnis') || '';
      const score = sessionStorage.getItem('fokustyp_score') || '';
      const datum = sessionStorage.getItem('fokustyp_quiz_datum') || '';
      if (ergebnis) setFokustyp(ergebnis);
      if (score) setFokustypScore(score);
      if (datum) setQuizDatum(datum);
    } catch (e) { /* SessionStorage nicht verfügbar */ }
    setGdprTimestamp(new Date().toISOString());
  }, []);

  useEffect(() => {
    // Captcha-Script lazy laden (DSGVO-konformer Spam-Schutz)
    if (typeof document === 'undefined') return;
    if (document.querySelector('script[data-kt-captcha]')) return;
    const s = document.createElement('script');
    s.src = 'https://scripts.cdn.klicktipp.cloud/captcha/protect.js';
    s.async = true;
    s.setAttribute('data-kt-captcha', '1');
    document.body.appendChild(s);
  }, []);

  const fokustypIndex = fokustyp
    ? (FOKUSTYP_TO_ZIELSEITE[fokustyp.toLowerCase()] || '')
    : '';

  const handleSubmit = async (e) => {
    // Fire-and-forget Lead-Capture parallel zum Klick-Tipp-Submit.
    try {
      const form = e.currentTarget;
      const fd = new FormData(form);
      const lead = {
        email: fd.get('email'),
        first_name: fd.get('fields[fieldFirstName]') || null,
        fokustyp: fokustyp || null,
        fokustyp_index: fokustypIndex || null,
        fokustyp_score: fokustypScore || null,
        quiz_datum: quizDatum || new Date().toISOString(),
        quelle,
        gdpr_timestamp: gdprTimestamp || new Date().toISOString(),
      };
      // Auf api-Client mit baseURL verlassen, aber non-blocking
      api.post('/lead-capture', lead).catch((err) => {
        // Lead-Capture-Fehler darf Klick-Tipp-Submit NICHT blockieren
        // eslint-disable-next-line no-console
        console.warn('[lead-capture]', err?.message || err);
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[lead-capture]', err?.message || err);
    }
    // KEIN preventDefault — Form geht regulär zu Klick-Tipp
  };

  return (
    <div
      id={`form-353253-wrapper-${quelle}`}
      className="max-w-md mx-auto my-8 p-6 bg-white rounded-lg border border-gray-200 shadow-sm"
      data-testid="klick-tipp-optin-wrapper"
      data-quelle={quelle}
    >
      <div className="mb-4">
        <p className="text-sm text-gray-500 mb-1">
          <span>{BRAND.name}</span> ·{' '}
          <span className="text-gray-400">Newsletter</span>
        </p>
        <h3
          className="text-lg font-semibold text-gray-900"
          data-testid="kt-headline"
        >
          {HEADLINES[quelle] || HEADLINES['landing-page-footer']}
        </h3>
        <p className="text-sm text-gray-600 mt-1" data-testid="kt-subtext">
          {SUBTEXTS[quelle] || SUBTEXTS['landing-page-footer']}
        </p>
      </div>

      <form
        id={`ktv2-form-353253-${quelle}`}
        acceptCharset="UTF-8"
        method="post"
        action={KT_ACTION_URL}
        target="_blank"
        className="space-y-4"
        onSubmit={handleSubmit}
        data-testid="klick-tipp-optin-form"
      >
        <input type="hidden" name="apikey" value={KT_API_KEY} />
        <input type="hidden" name="fields[fieldDigit]" value="" />

        {/* 6 Custom-Fields */}
        <input
          type="hidden"
          name="fields[field203780]"
          value={fokustypIndex}
          data-testid="kt-quest-funnel-zielseite"
        />
        <input
          type="hidden"
          name="fields[fieldFokustyp]"
          value={fokustyp}
          data-testid="kt-fokustyp"
        />
        <input
          type="hidden"
          name="fields[fieldFokustypScore]"
          value={fokustypScore}
          data-testid="kt-fokustyp-score"
        />
        <input
          type="hidden"
          name="fields[fieldQuizDatum]"
          value={quizDatum || new Date().toISOString()}
          data-testid="kt-quiz-datum"
        />
        <input
          type="hidden"
          name="fields[fieldQuelle]"
          value={quelle}
          data-testid="kt-quelle"
        />
        <input
          type="hidden"
          name="fields[fieldGDPRTimestamp]"
          value={gdprTimestamp}
          data-testid="kt-gdpr-timestamp"
        />

        <div>
          <label
            htmlFor={`kt-firstname-${quelle}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Vorname (optional)
          </label>
          <input
            type="text"
            id={`kt-firstname-${quelle}`}
            name="fields[fieldFirstName]"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="kt-firstname-input"
          />
        </div>

        <div>
          <label
            htmlFor={`kt-email-${quelle}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Deine E-Mail-Adresse *
          </label>
          <input
            type="email"
            id={`kt-email-${quelle}`}
            name="email"
            required
            size={40}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="kt-email-input"
          />
        </div>

        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id={`kt-gdpr-${quelle}`}
            name="fields[DSGVOCheckbox]"
            className="ktv2-dsgvo-checkbox mt-1"
            data-testid="kt-gdpr-checkbox"
            required
          />
          <label
            htmlFor={`kt-gdpr-${quelle}`}
            className="text-sm text-gray-600"
          >
            Mit der Registrierung bestätigst du die Verarbeitung deiner Daten
            gemäß der{' '}
            <a
              href={DATENSCHUTZ_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
              data-testid="kt-datenschutz-link"
            >
              Datenschutzerklärung
            </a>
            .
          </label>
        </div>

        <div className="pt-2">
          <input
            type="submit"
            name="FormSubmit"
            value="Ergebnisse + Newsletter abonnieren"
            className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
            data-testid="kt-submit-button"
          />
        </div>
      </form>
    </div>
  );
}

export default KlickTippOptin;
